import { Alert, alpha, Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useEnergyConsumptionCsvDownloader } from '@/features/energyxplorer/hooks/useNodeConsumptionCsvDownload';
import { useTheme } from '@emotion/react';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import InsetDrawer from '@/components/drawer/InsetDrawer';
import DataStreamDetailList from './DataStreamDetailList';
import NodeInitiativeInclusionList from './NodeInitiativeInclusionList';
import EnergyTreeNodeDetailSummary from './EnergyTreeNodeDetailSummary';

const InfoNodeDrawer = ({
  open,
  onClose,
  clickedNode,
  selectedEnergyGroup,
  units,
  setValue,
}) => {
  const isResidual = clickedNode?.options !== null;
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-tree-tab', 'common');
  const { downloadEnergyCsv } = useEnergyConsumptionCsvDownloader();

  const unitsOnNode = units.filter((unit) =>
    clickedNode?.dataStreams?.some(
      (dataStream) => dataStream.unitId === unit.UnitId,
    ),
  );

  return (
    <InsetDrawer
      open={open}
      onClose={onClose}
      title={'Detaljer'}
      content={
        <Stack spacing={2}>
          <EnergyTreeNodeDetailSummary clickedNode={clickedNode} />

          <DataStreamDetailList dataStreams={unitsOnNode} />

          <NodeInitiativeInclusionList
            clickedNode={clickedNode}
            energyGroup={selectedEnergyGroup}
          />

          <Box display="flex" justifyContent="flex-start">
            <Box display="flex" flexDirection="column" flexGrow={1}>
              {clickedNode?.dataStreams?.length === 0 && !isResidual && (
                <Alert
                  severity="info"
                  sx={(theme) => ({
                    mt: 1,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    borderLeft: `6px solid ${theme.palette.info.main}`,
                  })}
                >
                  Denne node er en{' '}
                  <b>
                    <u>virtuel node</u>
                  </b>
                  , hvilket betyder at den ikke har nogen direkte datakilder
                  tilknyttet. Den kan bruges til at strukturere og organisere
                  andre noder og deres data i træstrukturen.
                </Alert>
              )}

              {isResidual && (
                <Alert
                  severity="info"
                  sx={(theme) => ({
                    mt: 1,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    borderLeft: `6px solid ${theme.palette.info.main}`,
                  })}
                >
                  {/* <AlertTitle>Note</AlertTitle> */}
                  {clickedNode?.options?.specialNodeType === 'FlatResidual' && (
                    <>
                      Denne node er en{' '}
                      <u>
                        <b>flad residual node</b>
                      </u>
                      , hvilket betyder at den har en fast anslået effekt. Det
                      er ikke muligt at redigere, flytte eller tilføje noder
                      under denne type node.
                    </>
                  )}

                  {clickedNode?.options?.specialNodeType ===
                    'PercentageResidual' && (
                    <>
                      Denne node er en{' '}
                      <u>
                        <b>procentuel residual node</b>
                      </u>
                      , hvilket betyder at den har en anslået effekt baseret på
                      en procentdel af de øvrige noder i gruppen. Det er ikke
                      muligt at redigere, flytte eller tilføje noder under denne
                      type node.
                    </>
                  )}

                  {clickedNode?.options?.specialNodeType === 'RestResidual' && (
                    <>
                      Denne node er en{' '}
                      <u>
                        <b>rest residual node</b>
                      </u>
                      , hvilket betyder at den repræsenterer den resterende del
                      af forbruget, som ikke er dækket af de andre noder i
                      gruppen. Det er ikke muligt at redigere, flytte eller
                      tilføje noder under denne type node.
                    </>
                  )}
                </Alert>
              )}

              <Box display="flex" mt={1} gap={1}>
                <Button
                  variant="text"
                  endIcon={
                    <KeyboardArrowRightRoundedIcon
                      sx={{ transition: 'margin-left 0.1s ease-in-out' }}
                    />
                  }
                  onClick={() => setValue(2)}
                  sx={{
                    '&:hover': {
                      color: theme.palette.text.primary,
                      '& .MuiSvgIcon-root': { ml: 0.5 },
                      transition: 'margin-left 0.3s ease-in-out',
                    },
                  }}
                >
                  {t(
                    'node_dialog.info_node_tab.actions.open_in_consumption_analysis',
                  )}
                </Button>
                <Button
                  variant="text"
                  endIcon={
                    <KeyboardArrowRightRoundedIcon
                      sx={{ transition: 'margin-left 0.1s ease-in-out' }}
                    />
                  }
                  onClick={() => downloadEnergyCsv(clickedNode)}
                  sx={{
                    '&:hover': {
                      color: theme.palette.text.primary,
                      '& .MuiSvgIcon-root': { ml: 0.5 },
                      transition: 'margin-left 0.3s ease-in-out',
                    },
                  }}
                >
                  {t('node_dialog.info_node_tab.actions.download_csv')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Stack>
      }
    />
  );
};

export default InfoNodeDrawer;
